import type {
  AccountNode,
  Car,
  CarEvent,
  CarStatistics,
  CarStatisticsPeriod,
  CreateInsurancePolicyInput,
  CreateMaintenanceScheduleInput,
  InsurancePolicy,
  Invalidations,
  MarkMaintenanceScheduleDoneInput,
  MaintenanceSchedule,
  RecordCarEventInput,
  UpdateCarEventInput,
  UpdateCarInput,
} from '@taler/api-client'
import { accountKeys, carKeys, entryKeys } from '@taler/api-client'
import type { Locale } from '@taler/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import {
  type AccountOption,
  flattenExpenseAccounts,
  flattenIncomeAccounts,
  flattenMoneyAccounts,
} from '../internal/accounts.ts'
import type { RecentEntryOption } from '../internal/entries.ts'
import { problemCode, problemDetail } from '../internal/problem.ts'

export type { RecentEntryOption } from '../internal/entries.ts'

export interface UseCarOptions {
  readonly ledgerId: () => string | undefined
  readonly carId: () => string
  readonly enabled: () => boolean
  readonly displayCurrency: () => string
  readonly period: () => CarStatisticsPeriod
  readonly locale: () => Locale
}

export interface UseCar {
  readonly car: ComputedRef<Car | undefined>
  readonly events: ComputedRef<readonly CarEvent[]>
  readonly policies: ComputedRef<readonly InsurancePolicy[]>
  readonly schedules: ComputedRef<readonly MaintenanceSchedule[]>
  readonly statistics: ComputedRef<CarStatistics | undefined>
  readonly isLoadingStatistics: ComputedRef<boolean>
  readonly statisticsError: ComputedRef<Error | null>
  readonly statisticsRetrying: ComputedRef<boolean>
  retryStatistics(): void
  readonly isReplacingPhoto: ComputedRef<boolean>
  readonly hasReplacePhotoError: ComputedRef<boolean>
  readonly replacePhotoErrorCode: ComputedRef<string | undefined>
  replacePhoto(file: File): Promise<Car>
  readonly isDeletingPhoto: ComputedRef<boolean>
  readonly isPhotoRequestPending: ComputedRef<boolean>
  deletePhoto(): Promise<void>
  readonly expenseAccounts: ComputedRef<readonly AccountOption[]>
  readonly sourceAccounts: ComputedRef<readonly AccountOption[]>
  readonly incomeAccounts: ComputedRef<readonly AccountOption[]>
  readonly recentEntries: ComputedRef<readonly RecentEntryOption[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>

  readonly isUpdatingCar: ComputedRef<boolean>
  readonly hasUpdateCarError: ComputedRef<boolean>
  readonly updateCarErrorDetail: ComputedRef<string | undefined>
  updateCar(input: UpdateCarInput): Promise<Car>
  readonly isDeletingCar: ComputedRef<boolean>
  deleteCar(): Promise<void>

  readonly isRecordingEvent: ComputedRef<boolean>
  readonly hasRecordEventError: ComputedRef<boolean>
  readonly recordEventErrorDetail: ComputedRef<string | undefined>
  recordEvent(input: RecordCarEventInput): Promise<CarEvent>
  deleteEvent(eventId: string): Promise<void>

  readonly isUpdatingEvent: ComputedRef<boolean>
  readonly hasUpdateEventError: ComputedRef<boolean>
  readonly updateEventErrorDetail: ComputedRef<string | undefined>
  updateEvent(eventId: string, input: UpdateCarEventInput): Promise<CarEvent>

  readonly isCreatingPolicy: ComputedRef<boolean>
  readonly hasCreatePolicyError: ComputedRef<boolean>
  readonly createPolicyErrorDetail: ComputedRef<string | undefined>
  createPolicy(input: CreateInsurancePolicyInput): Promise<InsurancePolicy>
  deletePolicy(policyId: string): Promise<void>

  readonly isCreatingSchedule: ComputedRef<boolean>
  readonly hasCreateScheduleError: ComputedRef<boolean>
  readonly createScheduleErrorDetail: ComputedRef<string | undefined>
  createSchedule(input: CreateMaintenanceScheduleInput): Promise<MaintenanceSchedule>
  deleteSchedule(scheduleId: string): Promise<void>
  markScheduleDone(scheduleId: string, input: MarkMaintenanceScheduleDoneInput): Promise<MaintenanceSchedule>

  retry(): void
}

export function useCar(options: UseCarOptions): UseCar {
  const api = useApi()
  const queryClient = useQueryClient()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const carQuery = useQuery({
    queryKey: computed(() => carKeys.detail(options.ledgerId() ?? '', options.carId())),
    queryFn: ({ signal }) => api.cars.get(options.ledgerId()!, options.carId(), signal),
    enabled: active,
    retry: false,
  })
  const car = computed(() => carQuery.data.value)

  const eventsQuery = useQuery({
    queryKey: computed(() => carKeys.events(options.ledgerId() ?? '', options.carId())),
    queryFn: ({ signal }) => api.cars.listEvents(options.ledgerId()!, options.carId(), signal),
    enabled: active,
    retry: false,
  })

  const policiesQuery = useQuery({
    queryKey: computed(() => carKeys.insurancePolicies(options.ledgerId() ?? '', options.carId())),
    queryFn: ({ signal }) => api.cars.listInsurancePolicies(options.ledgerId()!, options.carId(), signal),
    enabled: active,
    retry: false,
  })

  const schedulesQuery = useQuery({
    queryKey: computed(() => carKeys.maintenanceSchedules(options.ledgerId() ?? '', options.carId())),
    queryFn: ({ signal }) => api.cars.listMaintenanceSchedules(options.ledgerId()!, options.carId(), signal),
    enabled: active,
    retry: false,
  })

  const statisticsQuery = useQuery({
    queryKey: computed(() =>
      carKeys.statistics(options.ledgerId() ?? '', options.carId(), {
        displayCurrency: options.displayCurrency(),
        period: options.period(),
      }),
    ),
    queryFn: ({ signal }) =>
      api.cars.statistics(
        options.ledgerId()!,
        options.carId(),
        { displayCurrency: options.displayCurrency(), period: options.period() },
        signal,
      ),
    enabled: active,
    retry: false,
  })

  const detailQueries = [carQuery, eventsQuery, policiesQuery, schedulesQuery]

  const accountsTreeQuery = useQuery({
    queryKey: computed(() => accountKeys.tree(options.ledgerId() ?? '')),
    queryFn: ({ signal }) => api.accounts.tree(options.ledgerId()!, signal),
    enabled: active,
    retry: false,
  })

  const entriesQuery = useQuery({
    queryKey: computed(() => entryKeys.list(options.ledgerId() ?? '', {})),
    queryFn: ({ signal }) => api.entries.list(options.ledgerId()!, {}, signal),
    enabled: active,
    retry: false,
  })

  function invalidateKeys(keys: Invalidations): Promise<void> {
    return Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey }))).then(
      () => undefined,
    )
  }

  function invalidate(): Promise<void> {
    const id = options.ledgerId()
    if (id === undefined) return Promise.resolve()
    return invalidateKeys(carKeys.invalidatedByWrite(id))
  }

  const updateCarMutation = useMutation({
    mutationFn: (input: UpdateCarInput) => api.cars.update(options.ledgerId()!, options.carId(), input),
    onSuccess: () => invalidate(),
  })
  const deleteCarMutation = useMutation({
    mutationFn: () => api.cars.delete(options.ledgerId()!, options.carId()),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: carKeys.detail(options.ledgerId()!, options.carId()) })
      return invalidate()
    },
  })

  const replacePhotoMutation = useMutation({
    mutationFn: ({ ledgerId, carId, file }: { ledgerId: string; carId: string; file: File }) =>
      api.cars.replacePhoto(ledgerId, carId, file),
    onSuccess: (_car, { ledgerId, carId }) =>
      invalidateKeys(carKeys.invalidatedByPhotoWrite(ledgerId, carId)),
  })
  const replacePhotoError = computed(() =>
    replacePhotoMutation.variables.value?.carId === options.carId() ? replacePhotoMutation.error.value : null,
  )
  const deletePhotoMutation = useMutation({
    mutationFn: ({ ledgerId, carId }: { ledgerId: string; carId: string }) =>
      api.cars.deletePhoto(ledgerId, carId),
    onSuccess: (_void, { ledgerId, carId }) => {
      replacePhotoMutation.reset()
      return invalidateKeys(carKeys.invalidatedByPhotoWrite(ledgerId, carId))
    },
  })

  const recordEventMutation = useMutation({
    mutationFn: (input: RecordCarEventInput) =>
      api.cars.recordEvent(options.ledgerId()!, options.carId(), input),
    onSuccess: () => invalidate(),
  })
  const deleteEventMutation = useMutation({
    mutationFn: (eventId: string) => api.cars.deleteEvent(options.ledgerId()!, options.carId(), eventId),
    onSuccess: () => invalidate(),
  })
  const updateEventMutation = useMutation({
    mutationFn: (args: { eventId: string; input: UpdateCarEventInput }) =>
      api.cars.updateEvent(options.ledgerId()!, options.carId(), args.eventId, args.input),
    onSuccess: () => invalidate(),
  })

  const createPolicyMutation = useMutation({
    mutationFn: (input: CreateInsurancePolicyInput) =>
      api.cars.createInsurancePolicy(options.ledgerId()!, options.carId(), input),
    onSuccess: () => invalidate(),
  })
  const deletePolicyMutation = useMutation({
    mutationFn: (policyId: string) =>
      api.cars.deleteInsurancePolicy(options.ledgerId()!, options.carId(), policyId),
    onSuccess: () => invalidate(),
  })

  const createScheduleMutation = useMutation({
    mutationFn: (input: CreateMaintenanceScheduleInput) =>
      api.cars.createMaintenanceSchedule(options.ledgerId()!, options.carId(), input),
    onSuccess: () => invalidate(),
  })
  const deleteScheduleMutation = useMutation({
    mutationFn: (scheduleId: string) =>
      api.cars.deleteMaintenanceSchedule(options.ledgerId()!, options.carId(), scheduleId),
    onSuccess: () => invalidate(),
  })
  const markScheduleDoneMutation = useMutation({
    mutationFn: (args: { scheduleId: string; input: MarkMaintenanceScheduleDoneInput }) =>
      api.cars.markMaintenanceScheduleDone(options.ledgerId()!, options.carId(), args.scheduleId, args.input),
    onSuccess: () => invalidate(),
  })

  return {
    car,
    events: computed(() => eventsQuery.data.value ?? []),
    policies: computed(() => policiesQuery.data.value ?? []),
    schedules: computed(() => schedulesQuery.data.value ?? []),
    statistics: computed(() => statisticsQuery.data.value),
    isLoadingStatistics: computed(() => active.value && statisticsQuery.isPending.value),
    statisticsError: computed(() => statisticsQuery.error.value),
    statisticsRetrying: retrying(statisticsQuery),
    retryStatistics: () => {
      void statisticsQuery.refetch()
    },
    isReplacingPhoto: computed(() => replacePhotoMutation.isPending.value),
    hasReplacePhotoError: computed(() => replacePhotoError.value !== null),
    replacePhotoErrorCode: computed(() => problemCode(replacePhotoError.value)),
    replacePhoto: (file) =>
      replacePhotoMutation.mutateAsync({ ledgerId: options.ledgerId()!, carId: options.carId(), file }),
    isDeletingPhoto: computed(
      () =>
        deletePhotoMutation.isPending.value && deletePhotoMutation.variables.value?.carId === options.carId(),
    ),
    isPhotoRequestPending: computed(
      () => replacePhotoMutation.isPending.value || deletePhotoMutation.isPending.value,
    ),
    deletePhoto: () =>
      deletePhotoMutation.mutateAsync({ ledgerId: options.ledgerId()!, carId: options.carId() }),
    expenseAccounts: computed(() =>
      flattenExpenseAccounts(accountsTreeQuery.data.value ?? ([] as AccountNode[]), options.locale()),
    ),
    sourceAccounts: computed(() =>
      flattenMoneyAccounts(accountsTreeQuery.data.value ?? ([] as AccountNode[]), options.locale()),
    ),
    incomeAccounts: computed(() =>
      flattenIncomeAccounts(accountsTreeQuery.data.value ?? ([] as AccountNode[]), options.locale()),
    ),
    recentEntries: computed(() =>
      (entriesQuery.data.value?.items ?? []).map((entry) => ({
        id: entry.id,
        label: entry.payee ?? entry.memo ?? entry.id.slice(0, 8),
      })),
    ),
    isLoading: computed(() => active.value && detailQueries.some((query) => query.isPending.value)),
    error: computed(
      () => detailQueries.map((query) => query.error.value).find((error) => error !== null) ?? null,
    ),
    retrying: retrying(...detailQueries),

    isUpdatingCar: computed(() => updateCarMutation.isPending.value),
    hasUpdateCarError: computed(() => updateCarMutation.error.value !== null),
    updateCarErrorDetail: computed(() => problemDetail(updateCarMutation.error.value)),
    updateCar: (input) => updateCarMutation.mutateAsync(input),
    isDeletingCar: computed(() => deleteCarMutation.isPending.value),
    deleteCar: () => deleteCarMutation.mutateAsync(),

    isRecordingEvent: computed(() => recordEventMutation.isPending.value),
    hasRecordEventError: computed(() => recordEventMutation.error.value !== null),
    recordEventErrorDetail: computed(() => problemDetail(recordEventMutation.error.value)),
    recordEvent: (input) => recordEventMutation.mutateAsync(input),
    deleteEvent: (eventId) => deleteEventMutation.mutateAsync(eventId),

    isUpdatingEvent: computed(() => updateEventMutation.isPending.value),
    hasUpdateEventError: computed(() => updateEventMutation.error.value !== null),
    updateEventErrorDetail: computed(() => problemDetail(updateEventMutation.error.value)),
    updateEvent: (eventId, input) => updateEventMutation.mutateAsync({ eventId, input }),

    isCreatingPolicy: computed(() => createPolicyMutation.isPending.value),
    hasCreatePolicyError: computed(() => createPolicyMutation.error.value !== null),
    createPolicyErrorDetail: computed(() => problemDetail(createPolicyMutation.error.value)),
    createPolicy: (input) => createPolicyMutation.mutateAsync(input),
    deletePolicy: (policyId) => deletePolicyMutation.mutateAsync(policyId),

    isCreatingSchedule: computed(() => createScheduleMutation.isPending.value),
    hasCreateScheduleError: computed(() => createScheduleMutation.error.value !== null),
    createScheduleErrorDetail: computed(() => problemDetail(createScheduleMutation.error.value)),
    createSchedule: (input) => createScheduleMutation.mutateAsync(input),
    deleteSchedule: (scheduleId) => deleteScheduleMutation.mutateAsync(scheduleId),
    markScheduleDone: (scheduleId, input) => markScheduleDoneMutation.mutateAsync({ scheduleId, input }),

    retry: () => {
      for (const query of detailQueries) void query.refetch()
    },
  }
}
